import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const AcceptTenancyInvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [message, setMessage] = useState('Processing your invitation...');

  const token = searchParams.get('token');

  useEffect(() => {
    const acceptInvite = async () => {
      if (authLoading) return;

      if (!currentUser) {
        // Redirect to login/signup if not authenticated
        // Pass the return URL to redirect back after login
        navigate(
          `/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`
        );
        return;
      }

      if (!token) {
        setStatus('error');
        setMessage('Invalid invitation link.');
        return;
      }

      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/acceptTenancyInvite`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ inviteId: token })
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to accept invitation');
        }

        setStatus('success');
        setMessage('Invitation accepted! You have been added to the property.');
        toast.success('Welcome to your new home!');

        // Redirect to tenant onboarding after a delay
        setTimeout(() => {
          navigate('/tenant-onboarding');
        }, 3000);
      } catch (error: any) {
        console.error('Error accepting invite:', error);
        setStatus('error');
        setMessage(
          error.message || 'An error occurred while accepting the invitation.'
        );
      }
    };

    acceptInvite();
  }, [currentUser, authLoading, token, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'loading' && (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900">
              Processing Invitation
            </h2>
            <p className="text-gray-500 mt-2">{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center">
            <CheckCircleIcon className="h-20 w-20 text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">Success!</h2>
            <p className="text-gray-600 mt-2">{message}</p>
            <p className="text-sm text-gray-400 mt-6">
              Redirecting to onboarding...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center">
            <XCircleIcon className="h-20 w-20 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">Error</h2>
            <p className="text-gray-600 mt-2">{message}</p>
            <button
              onClick={() => navigate('/browse')}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptTenancyInvitePage;
